import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetDietDetailsQuery,
  useGetDietUnassignedSubmittedLogbooksQuery,
  useAssignDietLogbooksToAssessorMutation,
} from "../api/logbookDietApi";
import DataTable from "../components/DataTable";

function pct(part: number, total: number) {
  if (!total) return 0;
  return Math.round((part / total) * 100);
}

function downloadCsv(filename: string, rows: Record<string, any>[]) {
  if (!rows.length) return;

  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(","),
    ...rows.map((r) =>
      headers
        .map((h) => {
          const v = r[h] ?? "";
          const s = String(v).replaceAll('"', '""');
          return `"${s}"`;
        })
        .join(",")
    ),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function printToPdf(title: string, htmlBody: string) {
  const w = window.open("", "_blank", "width=1100,height=750");
  if (!w) return;

  w.document.write(`
    <html>
      <head>
        <title>${title}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 18px; }
          h1 { font-size: 18px; margin: 0 0 6px; }
          h2 { font-size: 14px; margin: 18px 0 6px; }
          .muted { color: #666; font-size: 12px; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th, td { border: 1px solid #ddd; padding: 8px; font-size: 12px; text-align: left; }
          th { background: #f5f5f5; }
        </style>
      </head>
      <body>${htmlBody}</body>
    </html>
  `);
  w.document.close();
  w.focus();
  w.print();
}

export default function LogbookDietDetailsPage() {
  const navigate = useNavigate();
  const { id = "" } = useParams();

  const { data, isLoading, error, refetch } = useGetDietDetailsQuery(id, {
    skip: !id,
  });

  const diet = data?.diet;
  const summary = data?.summary;
  const assessors = data?.assessors || [];
  const logbooks = data?.logbooks || [];
  const applications = data?.applications || [];

  const total = summary?.total_logbooks || 0;
  const assessed = summary?.assessed || 0;
  const pending = summary?.pending || 0;

  const assessedPct = pct(assessed, total);
  const pendingPct = pct(pending, total);
  const submittedPct = pct(pending + assessed, total);

  const doughnutStyle = useMemo(() => {
    const a = assessedPct;
    return {
      background: `conic-gradient(#111 ${a}%, #e5e7eb 0)`,
    } as React.CSSProperties;
  }, [assessedPct]);

  // ==========================
  // ✅ Assign Submitted (Unassigned) Logbooks → Diet Assessor
  // ==========================
  const [openAssign, setOpenAssign] = useState(false);
  const [selectedAssessorMemberId, setSelectedAssessorMemberId] = useState("");
  const [pickedLogbooks, setPickedLogbooks] = useState<string[]>([]);
  const [assignErr, setAssignErr] = useState<string | null>(null);
  const [assignOk, setAssignOk] = useState<string | null>(null);

  const {
    data: unassignedRes,
    isFetching: loadingUnassigned,
    refetch: refetchUnassigned,
  } = useGetDietUnassignedSubmittedLogbooksQuery(
    { dietId: id },
    { skip: !openAssign || !id }
  );

  // backend response could be:
  // { diet_id, total, data: [...] }  OR maybe already { data: [...] }
  const unassignedLogbooks = useMemo(() => {
    if (!unassignedRes) return [];
    if (Array.isArray(unassignedRes)) return unassignedRes;
    if (Array.isArray((unassignedRes as any)?.data))
      return (unassignedRes as any).data;
    if (Array.isArray((unassignedRes as any)?.data?.data))
      return (unassignedRes as any).data.data;
    return [];
  }, [unassignedRes]);

  const [assignLogbooks, { isLoading: assigning }] =
    useAssignDietLogbooksToAssessorMutation();

  const openAssignModal = () => {
    setAssignErr(null);
    setAssignOk(null);
    setPickedLogbooks([]);
    setSelectedAssessorMemberId("");
    setOpenAssign(true);
  };

  const toggleLogbook = (logbookId: string) => {
    setPickedLogbooks((prev) =>
      prev.includes(logbookId)
        ? prev.filter((x) => x !== logbookId)
        : [...prev, logbookId]
    );
  };

  const assignSelected = async () => {
    if (!id) return;
    setAssignErr(null);
    setAssignOk(null);

    if (!selectedAssessorMemberId) {
      setAssignErr("Please select an assessor.");
      return;
    }
    if (!pickedLogbooks.length) {
      setAssignErr("Please select at least one logbook.");
      return;
    }

    try {
      const res = await assignLogbooks({
        dietId: id,
        assessor_member_id: selectedAssessorMemberId,
        logbook_ids: pickedLogbooks,
      }).unwrap();

      setAssignOk(res?.message || "Logbooks assigned successfully");
      setPickedLogbooks([]);

      // refresh diet details + unassigned list
      await refetch();
      await refetchUnassigned();
    } catch (e: any) {
      setAssignErr(
        String(
          e?.data?.message ||
            e?.message ||
            e?.error ||
            "Failed to assign logbooks"
        )
      );
    }
  };

  const exportSummaryCsv = () => {
    if (!diet) return;
    downloadCsv(`diet_${diet.id}_summary.csv`, [
      {
        id: diet.id,
        title: diet.title,
        status: diet.status,
        start_date: diet.start_date,
        end_date: diet.end_date,
        is_active: diet.is_active,
        notes: diet.notes ?? "",
        total_logbooks: total,
        assessed,
        pending,
        total_assessors: summary?.total_assessors ?? 0,
      },
    ]);
  };

  const exportAssessorsCsv = () => {
    const rows = assessors.map((a: any) => ({
      member_id: a.member_id,
      name: a.name,
      assigned: a.total_assigned_logbooks,
      assessed: a.total_assessed_logbooks,
      pending: a.total_pending_logbooks,
    }));
    downloadCsv(`diet_${id}_assessors.csv`, rows);
  };

  const exportLogbooksCsv = () => {
    const rows = logbooks.map((lb: any) => ({
      id: lb.id,
      user_id: lb.user_id,
      application_id: lb.application_id,
      stage: lb.stage,
      status: lb.status,
      total_score: lb.total_score ?? "",
      remarks: lb.remarks ?? "",
      supervisor_id: lb.supervisor_id ?? "",
      assessor_id: lb.assessor_id ?? "",
      assessed_by: lb.assessed_by ?? "",
      created_at: lb.created_at,
      updated_at: lb.updated_at,
    }));
    downloadCsv(`diet_${id}_logbooks.csv`, rows);
  };

  const exportPdf = () => {
    if (!diet) return;

    const html = `
      <h1>Logbook Diet Report</h1>
      <div class="muted">${diet.title} • ${diet.start_date} → ${
      diet.end_date
    } • status: ${diet.status}</div>

      <h2>Summary</h2>
      <table>
        <thead><tr><th>Metric</th><th>Value</th></tr></thead>
        <tbody>
          <tr><td>Total Logbooks</td><td>${total}</td></tr>
          <tr><td>Assessed</td><td>${assessed}</td></tr>
          <tr><td>Pending</td><td>${pending}</td></tr>
          <tr><td>Total Assessors</td><td>${
            summary?.total_assessors ?? 0
          }</td></tr>
        </tbody>
      </table>

      <h2>Assessors</h2>
      <table>
        <thead><tr><th>Name</th><th>Assigned</th><th>Assessed</th><th>Pending</th></tr></thead>
        <tbody>
          ${assessors
            .map(
              (a: any) =>
                `<tr><td>${a.name}</td><td>${a.total_assigned_logbooks}</td><td>${a.total_assessed_logbooks}</td><td>${a.total_pending_logbooks}</td></tr>`
            )
            .join("")}
        </tbody>
      </table>
    `;

    printToPdf(`Diet_${diet.title}`, html);
  };

  if (isLoading) return <div className="text-gray-600">Loading diet...</div>;
  if (error) return <div className="text-red-600">Failed to load diet.</div>;
  if (!data || !diet) return <div className="text-gray-600">No diet data.</div>;

  return (
    <div className="bg-white border rounded-xl p-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <button
            onClick={() => navigate("/logbook-diets")}
            className="text-sm text-gray-600 hover:underline"
          >
            ← Back to Diets
          </button>

          <h1 className="text-xl font-bold text-gray-800 mt-2">{diet.title}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {diet.start_date} → {diet.end_date} • status:{" "}
            <span className="font-semibold">{diet.status}</span>
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => refetch()}
            className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm"
          >
            Refresh
          </button>

          <button
            onClick={exportSummaryCsv}
            className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm"
          >
            Export Summary CSV
          </button>

          <button
            onClick={exportAssessorsCsv}
            className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm"
          >
            Export Assessors CSV
          </button>

          <button
            onClick={exportLogbooksCsv}
            className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm"
          >
            Export Logbooks CSV
          </button>

          <button
            onClick={exportPdf}
            className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 text-sm"
          >
            Export PDF
          </button>
        </div>
      </div>

      {/* KPI + Charts */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="border rounded-xl p-4">
          <div className="text-xs text-gray-500">Total Logbooks</div>
          <div className="text-2xl font-bold mt-1">{total}</div>
        </div>

        <div className="border rounded-xl p-4">
          <div className="text-xs text-gray-500">Assessed</div>
          <div className="text-2xl font-bold mt-1">{assessed}</div>
          <div className="text-sm text-gray-500 mt-1">{assessedPct}%</div>
          <div className="mt-3 h-2 rounded bg-gray-200 overflow-hidden">
            <div
              className="h-2 bg-gray-900"
              style={{ width: `${assessedPct}%` }}
            />
          </div>
        </div>

        <div className="border rounded-xl p-4">
          <div className="text-xs text-gray-500">Pending</div>
          <div className="text-2xl font-bold mt-1">{pending}</div>
          <div className="text-sm text-gray-500 mt-1">{pendingPct}%</div>
          <div className="mt-3 h-2 rounded bg-gray-200 overflow-hidden">
            <div
              className="h-2 bg-gray-500"
              style={{ width: `${pendingPct}%` }}
            />
          </div>
        </div>

        <div className="border rounded-xl p-4">
          <div className="text-xs text-gray-500">Submitted</div>
          <div className="text-2xl font-bold mt-1">{pending + assessed}</div>
          <div className="text-sm text-gray-500 mt-1">{submittedPct}%</div>
          <div className="mt-3 h-2 rounded bg-gray-200 overflow-hidden">
            <div
              className="h-2 bg-gray-500"
              style={{ width: `${submittedPct}%` }}
            />
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="border rounded-xl p-4 flex items-center gap-4">
          <div className="w-16 h-16 rounded-full" style={doughnutStyle} />
          <div>
            <div className="font-semibold text-gray-800">
              Submitted vs Assessed
            </div>
            <div className="text-sm text-gray-500 mt-1">
              Submitted: {assessed + pending} • Assessed: {assessed}
            </div>
          </div>
        </div>

        {/* Assessors + Assign */}
        <div className="border rounded-xl p-4 lg:col-span-2">
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="font-semibold text-gray-800">Assessors</div>
              <div className="text-sm text-gray-500">
                Click an assessor to view their assigned logbooks.
              </div>
            </div>

            <button
              onClick={openAssignModal}
              className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 text-sm"
            >
              Assign Submitted Logbooks
            </button>
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2">Name</th>
                  <th className="py-2">Assigned</th>
                  <th className="py-2">Assessed</th>
                  <th className="py-2">Pending</th>
                  <th className="py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {assessors.map((a: any) => (
                  <tr key={a.member_id} className="border-b">
                    <td className="py-3 font-semibold text-gray-800">
                      {a.name}
                    </td>
                    <td className="py-3">{a.total_assigned_logbooks}</td>
                    <td className="py-3">{a.total_assessed_logbooks}</td>
                    <td className="py-3">{a.total_pending_logbooks}</td>
                    <td className="py-3">
                      <button
                        className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 text-xs"
                        onClick={() =>
                          navigate(
                            `/logbook-diets/${id}/assessors/${a.member_id}/logbooks`
                          )
                        }
                      >
                        View logbooks
                      </button>
                    </td>
                  </tr>
                ))}

                {!assessors.length ? (
                  <tr>
                    <td colSpan={5} className="py-6 text-gray-500">
                      No assessors assigned.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Logbooks */}
      <DataTable
        title="Logbooks"
        subtitle="Filter by search, sort columns, paginate."
        data={logbooks}
        status={{ key: "status", label: "Status" }}
        initialPageSize={10}
        columns={[
          {
            key: "user_id",
            header: "User",
            sortable: true,
            accessor: (r: any) =>
              `${r?.application?.other_names ?? ""} ${
                r?.application?.surname ?? ""
              }`.trim(),
          },
          {
            key: "stage",
            header: "Stage",
            sortable: true,
            accessor: (r: any) => r.stage,
          },
          {
            key: "status",
            header: "Status",
            sortable: true,
            accessor: (r: any) => r.status,
          },
          {
            key: "assessor_id",
            header: "Assessor",
            sortable: true,
            accessor: (r: any) =>
              r?.assessor
                ? `${r.assessor.firstname ?? ""} ${
                    r.assessor.othernames ?? ""
                  } ${r.assessor.surname ?? ""}`.trim()
                : "N/A",
          },
          {
            key: "total_score",
            header: "Score",
            sortable: true,
            accessor: (r: any) => r.total_score ?? "",
          },
          {
            key: "action",
            header: "Action",
            sortable: false,
            cell: (lb: any) => (
              <button
                className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 text-xs"
                onClick={() => navigate(`/assessor/logbook/${lb.id}`)}
              >
                View
              </button>
            ),
          },
        ]}
        emptyText="No logbooks found."
      />

      {/* Applications */}
      <DataTable
        title="Applications"
        subtitle="Filter by search, sort columns, paginate."
        data={applications}
        status={{ key: "status", label: "Status" }}
        initialPageSize={10}
        searchKeys={["surname", "other_names", "email", "phone", "status"]}
        columns={[
          {
            key: "name",
            header: "Name",
            sortable: true,
            accessor: (r: any) =>
              `${r.surname ?? ""} ${r.other_names ?? ""}`.trim(),
            cell: (r: any) => (
              <span className="font-semibold text-gray-800">
                {r.surname} {r.other_names}
              </span>
            ),
          },
          {
            key: "email",
            header: "Email",
            sortable: true,
            accessor: (r: any) => r.email,
          },
          {
            key: "phone",
            header: "Phone",
            sortable: true,
            accessor: (r: any) => r.phone,
          },
          {
            key: "status",
            header: "Status",
            sortable: true,
            accessor: (r: any) => r.status,
          },
          {
            key: "approved_at",
            header: "Approved At",
            sortable: true,
            accessor: (r: any) => r.approved_at ?? "—",
          },
          {
            key: "action",
            header: "Action",
            sortable: false,
            cell: (a: any) => (
              <button
                className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 text-xs"
                onClick={() =>
                  navigate(`/admin-probationer-applications/${a.id}`)
                }
              >
                View
              </button>
            ),
          },
        ]}
        emptyText="No applications."
      />

      {/* ✅ Assign Modal */}
      {openAssign ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-4xl rounded-xl bg-white border shadow-lg p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold">
                  Assign Submitted Logbooks
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Select an assessor in this diet, then pick submitted logbooks
                  that are not assigned yet.
                </p>
              </div>

              <button
                onClick={() => setOpenAssign(false)}
                className="px-2 py-1 rounded border hover:bg-gray-50"
              >
                ✕
              </button>
            </div>

            {assignErr ? (
              <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-100 rounded p-3">
                {assignErr}
              </div>
            ) : null}

            {assignOk ? (
              <div className="mt-3 text-xs text-green-700 bg-green-50 border border-green-100 rounded p-3">
                {assignOk}
              </div>
            ) : null}

            <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* LEFT: Diet Assessors */}
              <div className="border rounded-xl p-4">
                <div className="font-semibold text-gray-800">
                  Diet Assessors
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Select who will assess the chosen logbooks.
                </div>

                <div className="mt-3 space-y-2 max-h-[340px] overflow-y-auto pr-1">
                  {assessors.map((a: any) => {
                    const memberId = String(a.member_id);
                    const active = selectedAssessorMemberId === memberId;

                    return (
                      <button
                        type="button"
                        key={memberId}
                        onClick={() => setSelectedAssessorMemberId(memberId)}
                        className={`w-full text-left rounded-lg border px-3 py-2 hover:bg-gray-50 ${
                          active ? "bg-gray-50 border-gray-400" : ""
                        }`}
                      >
                        <div className="text-sm font-semibold text-gray-800">
                          {a.name}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Assigned: {a.total_assigned_logbooks} • Pending:{" "}
                          {a.total_pending_logbooks} • Assessed:{" "}
                          {a.total_assessed_logbooks}
                        </div>
                      </button>
                    );
                  })}

                  {!assessors.length ? (
                    <div className="text-sm text-gray-500">
                      No assessors assigned to this diet yet.
                    </div>
                  ) : null}
                </div>
              </div>

              {/* RIGHT: Unassigned submitted logbooks */}
              <div className="border rounded-xl p-4">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="font-semibold text-gray-800">
                      Unassigned Submitted Logbooks
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Select logbooks to assign.
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => refetchUnassigned()}
                    className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 text-xs"
                  >
                    Refresh
                  </button>
                </div>

                <div className="mt-3 max-h-[340px] overflow-y-auto pr-1 space-y-2">
                  {loadingUnassigned ? (
                    <div className="text-sm text-gray-500">Loading…</div>
                  ) : (
                    unassignedLogbooks.map((lb: any) => {
                      const checked = pickedLogbooks.includes(lb.id);
                      const name = `${lb?.application?.surname ?? ""} ${
                        lb?.application?.other_names ?? ""
                      }`.trim();

                      return (
                        <label
                          key={lb.id}
                          className={`flex items-start gap-3 rounded-lg border px-3 py-2 cursor-pointer hover:bg-gray-50 ${
                            checked ? "bg-gray-50" : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleLogbook(lb.id)}
                            className="h-4 w-4 mt-1"
                          />
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-gray-800 truncate">
                              {name || lb.user_id}
                            </div>
                            <div className="text-xs text-gray-500">
                              Stage {lb.stage} • Status: {lb.status}
                            </div>
                          </div>
                        </label>
                      );
                    })
                  )}

                  {!loadingUnassigned && !unassignedLogbooks.length ? (
                    <div className="text-sm text-gray-500">
                      No submitted logbooks waiting for assignment.
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Footer actions */}
            <div className="mt-5 flex items-center justify-between gap-2">
              <div className="text-xs text-gray-600">
                Selected logbooks: <b>{pickedLogbooks.length}</b>
                {selectedAssessorMemberId ? (
                  <> • Assessor selected ✅</>
                ) : (
                  <> • Select an assessor</>
                )}
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setPickedLogbooks([]);
                    setSelectedAssessorMemberId("");
                    setAssignErr(null);
                    setAssignOk(null);
                  }}
                  className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm"
                  disabled={assigning}
                >
                  Clear
                </button>

                <button
                  type="button"
                  disabled={
                    !selectedAssessorMemberId ||
                    !pickedLogbooks.length ||
                    assigning
                  }
                  onClick={assignSelected}
                  className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 text-sm disabled:opacity-60"
                >
                  {assigning ? "Assigning..." : "Assign"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
